import React, { useState, useEffect } from 'react';
import { UserProfile, VideoLog } from '../../types';
import { supabase } from '../../lib/supabase';
import { ChevronDown, ChevronRight, Video, Package, AlertCircle, Loader2 } from 'lucide-react';

// --- HELPER: CONVERT UTC TO IST DISPLAY ---
const formatToIST = (utcDateString: string) => {
  if (!utcDateString) return '-';
  const date = new Date(utcDateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

interface ExtendedLog extends VideoLog {
  order_id?: string;
  customer_name?: string;
  customer_email?: string;
  customer_mobile?: string;
  
  // Matches your DB schema exactly
  Address?: string;
  address2?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  
  order_amount?: number;
  "Product Type"?: string;
  
  payment_mode?: string;
  delivery_status?: string;
  shopify_fetch_status?: string;
}

export const HistoryTab: React.FC<{ user: UserProfile }> = ({ user }) => {
  const [logs, setLogs] = useState<ExtendedLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const itemsPerPage = 50;

  useEffect(() => {
    fetchLogs();
  }, [user.id]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      // Fetch data using exact column names
      const { data, error } = await supabase
        .from('logs')
        .select(`
          id,
          created_at,
          order_id,
          customer_name,
          customer_mobile,
          "Address",
          address2,
          city,
          state,
          country,
          pincode,
          order_amount,
          "Product Type",
          payment_mode,
          delivery_status,
          shopify_fetch_status,
          video_url,
          awb,
          packer:packer_id ( name )
        `)
        .gte('created_at', thirtyDaysAgo.toISOString())
        .order('created_at', { ascending: false });

      if (error) throw error;
      setLogs(data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const totalPages = Math.ceil(logs.length / itemsPerPage);
  const currentLogs = logs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-800">Order History</h2>
        <button 
          onClick={fetchLogs} 
          className="px-4 py-2 text-sm bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
        >
          Refresh Data
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-4 font-semibold text-slate-600">Date (IST)</th>
                <th className="px-6 py-4 font-semibold text-slate-600">Order ID</th>
                <th className="px-6 py-4 font-semibold text-slate-600">AWB</th>
                <th className="px-6 py-4 font-semibold text-slate-600">Customer</th>
                <th className="px-6 py-4 font-semibold text-slate-600">Packer</th>
                <th className="px-6 py-4 font-semibold text-slate-600">Status</th>
                <th className="px-6 py-4 font-semibold text-slate-600">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentLogs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-slate-600 font-medium whitespace-nowrap">
                      {/* APPLYING IST FORMATTER HERE */}
                      {formatToIST(log.created_at)}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900">
                      {log.order_id || '-'}
                    </td>
                    <td className="px-6 py-4 font-mono text-slate-600">
                      {log.awb}
                    </td>
                    <td className="px-6 py-4 text-slate-900">
                      {log.customer_name || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                        {/* @ts-ignore */}
                        {log.packer?.name || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {log.shopify_fetch_status === 'Found' ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                          Synced
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                          {log.shopify_fetch_status || 'Pending'}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleRow(log.id)}
                        className="p-1 hover:bg-slate-200 rounded-full transition-colors"
                      >
                        {expandedRows.has(log.id) ? (
                          <ChevronDown size={18} className="text-slate-500" />
                        ) : (
                          <ChevronRight size={18} className="text-slate-500" />
                        )}
                      </button>
                    </td>
                  </tr>
                  
                  {expandedRows.has(log.id) && (
                    <tr className="bg-slate-50/50">
                      <td colSpan={7} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                          
                          {/* Shipping Details */}
                          <div className="col-span-1 space-y-1">
                            <h4 className="font-semibold text-slate-900 mb-2">Shipping Address</h4>
                            <p className="text-slate-600">{log["Address"]}</p>
                            {log.address2 && <p className="text-slate-600">{log.address2}</p>}
                            <p className="text-slate-600">
                              {[log.city, log.state, log.pincode].filter(Boolean).join(', ')}
                            </p>
                            <p className="text-slate-600">{log.country}</p>
                            <p className="text-slate-600 mt-1 flex items-center gap-1">
                              📞 {log.customer_mobile || 'N/A'}
                            </p>
                          </div>

                          {/* Order Details */}
                          <div className="col-span-1 space-y-1">
                            <h4 className="font-semibold text-slate-900 mb-2">Order Info</h4>
                            <p className="text-slate-600">
                              Amount: <span className="font-medium">₹{log.order_amount}</span>
                            </p>
                            <p className="text-slate-600">
                              Payment: {log.payment_mode}
                            </p>
                            <p className="text-slate-600">
                              Status: {log.delivery_status}
                            </p>
                          </div>

                          {/* Products */}
                          <div className="col-span-1">
                            <h4 className="font-semibold text-slate-900 mb-2">Items</h4>
                            <div className="bg-white p-3 rounded-lg border border-slate-200">
                              <p className="text-slate-700 font-medium">
                                {log["Product Type"] || "No items found"}
                              </p>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="col-span-1 flex flex-col justify-end gap-2">
                             {log.video_url ? (
                               <a 
                                 href={log.video_url} 
                                 target="_blank" 
                                 rel="noreferrer"
                                 className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                               >
                                 <Video size={16} /> Watch Video
                               </a>
                             ) : (
                               <div className="flex items-center gap-2 text-amber-600 text-xs bg-amber-50 p-2 rounded">
                                 <AlertCircle size={14} /> Processing Video...
                               </div>
                             )}
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* ... Pagination Logic (No Changes) ... */}
         {logs.length > 0 && (
          <div className="bg-slate-50 border-t border-slate-200 px-4 py-3 flex items-center justify-between">
            <p className="text-sm text-slate-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
              {Math.min(currentPage * itemsPerPage, logs.length)} of {logs.length} entries
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                Previous
              </button>
              <span className="px-3 py-1 text-sm text-slate-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

      </div>

      {logs.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <Package size={48} className="mx-auto mb-4 opacity-50" />
          <p>No order history found for the last 30 days.</p>
        </div>
      )}
    </div>
  );
};
